import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const timeoutRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.substring(1));
    const sessionId = params.get('session_id');

    if (!sessionId) {
      setError('Sesion invalida - no se encontro session_id');
      toast.error('Sesion invalida');
      setTimeout(() => navigate('/login', { replace: true }), 2000);
      return;
    }

    // Safety timeout: if nothing happens in 20 seconds, abort
    timeoutRef.current = setTimeout(() => {
      setError('La autenticacion esta tardando demasiado. Intenta de nuevo.');
      toast.error('Timeout de autenticacion');
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    }, 20000);

    const processSession = async () => {
      try {
        const { data } = await axios.post(
          `${BACKEND_URL}/api/auth/session`,
          { session_id: sessionId },
          { withCredentials: true, timeout: 15000 }
        );

        // Clear safety timeout
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        // Clear hash from URL BEFORE any state updates
        window.history.replaceState(null, '', window.location.pathname);

        // Set user in context
        setUser(data);
        toast.success('Autenticado con Google!');

        // Check if new user needs to select a plan
        if (data.requires_checkout) {
          navigate('/plans', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      } catch (err) {
        // Clear safety timeout
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        const errorMsg = err.response?.data?.detail || err.message || 'Error desconocido';
        console.error('Google auth error:', errorMsg);
        setError(`Error: ${errorMsg}`);
        toast.error('Error al autenticar con Google');

        // Clear hash to prevent retry loop
        window.history.replaceState(null, '', '/login');
        setTimeout(() => navigate('/login', { replace: true }), 3000);
      }
    };

    processSession();

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [navigate, setUser, BACKEND_URL]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md" data-testid="auth-callback-error">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-950 mb-2">Error de autenticacion</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">Redirigiendo al login...</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="mt-4 text-indigo-600 hover:text-indigo-700 font-medium text-sm"
            data-testid="auth-callback-back-btn"
          >
            Volver al login ahora
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center" data-testid="auth-callback-loading">
        <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
        <p className="text-gray-600">Completando autenticacion...</p>
        <p className="text-sm text-gray-400 mt-2">Esto solo toma unos segundos</p>
      </div>
    </div>
  );
};

export default AuthCallback;
